import React, { useState } from 'react';
import { Users, Settings, Database, Activity, Shield, AlertTriangle, BarChart3, Plus, Edit, Trash2, Search } from 'lucide-react';
import './Administration.css';

const Administration = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data
  const users = [
    { id: 1, name: 'Dr. John Smith', email: 'john.smith@hido.com', role: 'Doctor', status: 'Active', lastLogin: '2 hours ago' },
    { id: 2, name: 'Nurse Sarah Johnson', email: 'sarah.j@hido.com', role: 'Nurse', status: 'Active', lastLogin: '1 day ago' },
    { id: 3, name: 'Admin Mike Wilson', email: 'mike.w@hido.com', role: 'Administrator', status: 'Active', lastLogin: '30 minutes ago' },
    { id: 4, name: 'Dr. Emily Davis', email: 'emily.d@hido.com', role: 'Doctor', status: 'Inactive', lastLogin: '1 week ago' }
  ];

  const systemStats = [
    { label: 'Total Users', value: '127', icon: Users, color: '#3b82f6' },
    { label: 'Active Sessions', value: '43', icon: Activity, color: '#16a34a' },
    { label: 'System Uptime', value: '99.9%', icon: BarChart3, color: '#f59e0b' },
    { label: 'Security Alerts', value: '2', icon: Shield, color: '#dc2626' }
  ];

  const recentLogs = [
    { id: 1, timestamp: '2025-01-28 14:30:25', user: 'Dr. Smith', action: 'Patient data accessed', level: 'Info' },
    { id: 2, timestamp: '2025-01-28 14:28:15', user: 'Admin Wilson', action: 'User permissions updated', level: 'Warning' },
    { id: 3, timestamp: '2025-01-28 14:25:10', user: 'System', action: 'Automated backup completed', level: 'Info' },
    { id: 4, timestamp: '2025-01-28 14:20:05', user: 'Nurse Johnson', action: 'Failed login attempt', level: 'Error' }
  ];

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderUsersTab = () => (
    <div className="admin-tab-content">
      <div className="tab-header">
        <div className="tab-title">
          <h2>User Management</h2>
          <p>Manage user accounts, roles, and permissions</p>
        </div>
        <button className="add-user-btn">
          <Plus size={16} />
          Add User
        </button>
      </div>

      <div className="search-section">
        <div className="search-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="users-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td className="user-name-cell">
                  <div className="user-avatar-small">
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  {user.name}
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge ${user.role.toLowerCase()}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span className={`status-indicator ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                </td>
                <td>{user.lastLogin}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit">
                      <Edit size={14} />
                    </button>
                    <button className="action-btn delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSystemTab = () => (
    <div className="admin-tab-content">
      <div className="tab-header">
        <div className="tab-title">
          <h2>System Overview</h2>
          <p>Monitor system health and performance metrics</p>
        </div>
      </div>

      <div className="stats-grid">
        {systemStats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon" style={{ color: stat.color }}>
              <stat.icon size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value" style={{ color: stat.color }}>{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="system-logs">
        <h3>Recent Activity Logs</h3>
        <div className="logs-container">
          {recentLogs.map(log => (
            <div key={log.id} className="log-entry">
              <div className="log-timestamp">{log.timestamp}</div>
              <div className="log-user">{log.user}</div>
              <div className="log-action">{log.action}</div>
              <div className={`log-level ${log.level.toLowerCase()}`}>
                {log.level}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="admin-tab-content">
      <div className="tab-header">
        <div className="tab-title">
          <h2>System Settings</h2>
          <p>Configure system-wide settings and preferences</p>
        </div>
      </div>

      <div className="settings-grid">
        <div className="setting-category">
          <h3>Security Settings</h3>
          <div className="setting-items">
            <div className="setting-row">
              <span>Password Requirements</span>
              <button className="configure-btn">Configure</button>
            </div>
            <div className="setting-row">
              <span>Session Timeout</span>
              <button className="configure-btn">Configure</button>
            </div>
            <div className="setting-row">
              <span>Two-Factor Authentication</span>
              <button className="configure-btn">Configure</button>
            </div>
          </div>
        </div>

        <div className="setting-category">
          <h3>System Configuration</h3>
          <div className="setting-items">
            <div className="setting-row">
              <span>Backup Schedule</span>
              <button className="configure-btn">Configure</button>
            </div>
            <div className="setting-row">
              <span>Data Retention Policy</span>
              <button className="configure-btn">Configure</button>
            </div>
            <div className="setting-row">
              <span>Email Templates</span>
              <button className="configure-btn">Configure</button>
            </div>
          </div>
        </div>

        <div className="setting-category">
          <h3>Integration Settings</h3>
          <div className="setting-items">
            <div className="setting-row">
              <span>API Configuration</span>
              <button className="configure-btn">Configure</button>
            </div>
            <div className="setting-row">
              <span>External Services</span>
              <button className="configure-btn">Configure</button>
            </div>
            <div className="setting-row">
              <span>Webhooks</span>
              <button className="configure-btn">Configure</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="administration-page">
      <div className="admin-header">
        <h1>Administration</h1>
        <p>Manage users, system settings, and monitor application health</p>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} />
          Users
        </button>
        <button
          className={`tab-button ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          <Database size={16} />
          System
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={16} />
          Settings
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && renderUsersTab()}
        {activeTab === 'system' && renderSystemTab()}
        {activeTab === 'settings' && renderSettingsTab()}
      </div>
    </div>
  );
};

export default Administration;

